import React from 'react';
import { Link } from 'react-router-dom';

import type { DomSessionRow } from '@/types/domSession';

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import QualificationStatusSelect from '@/components/sessions/QualificationStatusSelect';
import { useUpdateSessionQualificationStatus } from '@/hooks/sessions/useUpdateSessionQualificationStatus';

interface SessionsListProps {
  sessions: DomSessionRow[];
  selectedSession: DomSessionRow | null;
  isLoading: boolean;
  onSessionSelect: (session: DomSessionRow) => void;
  onSessionHover?: (session: DomSessionRow) => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SummarySkeleton = () => (
  <div className="space-y-2 border card-border card-bg-light p-4 rounded-lg">
    <div className="flex items-center justify-between gap-2">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-5 w-16" />
    </div>
    <Skeleton className="h-3 w-48" />
    <Skeleton className="h-3 w-56" />
  </div>
);

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  selectedSession,
  isLoading,
  onSessionSelect,
  onSessionHover,
}) => {
  const updateQualificationStatus = useUpdateSessionQualificationStatus();

  return (
    <div className="h-full card-bg">
      <ScrollArea className="h-full">
        {isLoading && !sessions.length && (
          <div className="space-y-0">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="px-4 py-2.5 border-b border-border">
                <Skeleton className="h-4 w-48 mb-1" />
                <Skeleton className="h-3 w-32" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !sessions.length && (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            No sessions yet.
          </div>
        )}

        <div>
          {sessions.map((session) => {
            const isSelected = selectedSession?.id === session.id;
            const email = session.userEmail || 'Anonymous';
            const salesSummary = (session as any).sales_summary;
            const updatedQualificationStatus =
              salesSummary?.updated_qualification_status ?? (session as any).updated_qualification_status;
            const qualificationStatus =
              updatedQualificationStatus ??
              salesSummary?.qualification_status ??
              (session as any).qualification_status;
            const name = salesSummary?.name || null;
            const date = formatDate(session.started_at);
            const durationS = session.duration_s;
            const duration = durationS != null
              ? durationS >= 60 ? `${Math.floor(durationS / 60)}m ${Math.round(durationS % 60)}s` : `${Math.round(durationS)}s`
              : null;

            const bookedStatus = salesSummary?.booked_status ?? (session as any).booked_status;
            const isBooked = bookedStatus === 'booked';

            const qualBadge = qualificationStatus === 'qualified'
              ? { label: 'Qualified', className: 'bg-success/10 text-success border-success/20' }
              : qualificationStatus === 'unqualified'
                ? { label: 'Not Qualified', className: 'bg-destructive/10 text-destructive border-destructive/20' }
                : null;

            return (
              <button
                key={session.id}
                type="button"
                onClick={() => onSessionSelect(session)}
                onMouseEnter={() => onSessionHover?.(session)}
                className={cn(
                  'w-full px-4 py-2.5 text-left transition-colors border-b border-border',
                  isSelected
                    ? 'bg-accent'
                    : 'hover:bg-muted/50'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">
                      {name || email}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {name ? email : ''}{name ? ' · ' : ''}{date}{duration ? ` · ${duration}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isBooked && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-5 font-medium bg-primary/10 text-primary border-primary/20"
                      >
                        Booked
                      </Badge>
                    )}
                    {qualBadge && (
                      <Badge
                        variant="outline"
                        className={cn('text-[10px] px-1.5 py-0 h-5 font-medium', qualBadge.className)}
                      >
                        {qualBadge.label}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SessionsList;
